import { useState } from "react";
import { Outlet, Link, useLocation } from "react-router-dom";
import { useIsMobile } from "@/hooks/use-mobile";
import { 
  LayoutDashboard, 
  Users, 
  Building2, 
  ShoppingBag, 
  Home,
  UserRound,
  CreditCard,
  BarChart3,
  Settings,
  Shield,
  ArrowLeft,
  FileText,
  UserCog,
  Ticket,
  Webhook,
  AlertTriangle,
  Activity,
  Plug,
  UserX,
  ShieldAlert,
  ClipboardCheck,
  HandCoins,
  Palette,
  Newspaper,
  ChevronLeft,
  ChevronRight,
  Key
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { AdminNotificationBell } from "@/components/admin/AdminNotificationBell";
import { AdminMobileNav } from "@/components/admin/AdminMobileNav";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const AdminLayout = () => {
  const isMobile = useIsMobile();
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(() => {
    return localStorage.getItem('admin-sidebar-collapsed') === 'true';
  });

  const toggleCollapse = () => {
    const newState = !isCollapsed;
    setIsCollapsed(newState);
    localStorage.setItem('admin-sidebar-collapsed', String(newState));
  };

  const navItems = [
    { icon: LayoutDashboard, label: "Dashboard", path: "/admin/dashboard" },
    { icon: UserX, label: "Abandonment", path: "/admin/abandonment" },
    { icon: BarChart3, label: "Analytics", path: "/admin/analytics" },
    { icon: Plug, label: "API Status", path: "/admin/api-status" },
    { icon: Newspaper, label: "Blog", path: "/admin/blog" },
    { icon: Key, label: "Blog API", path: "/admin/blog-api" },
    { icon: Palette, label: "Brand Kit", path: "/admin/brand-kit" },
    { icon: ShoppingBag, label: "Buyers", path: "/admin/buyers" },
    { icon: FileText, label: "Contracts", path: "/admin/contracts" },
    { icon: Activity, label: "Edge Function Logs", path: "/admin/edge-logs" },
    { icon: UserRound, label: "Homeowners", path: "/admin/homeowners" },
    { icon: ClipboardCheck, label: "Maintenance", path: "/admin/maintenance" },
    { icon: HandCoins, label: "Offers", path: "/admin/offers" },
    { icon: Ticket, label: "Promo Codes", path: "/admin/promo-codes" },
    { icon: Home, label: "Properties", path: "/admin/properties" },
    { icon: ShieldAlert, label: "Rate Limits", path: "/admin/rate-limits" },
    { icon: Building2, label: "Sellers", path: "/admin/sellers" },
    { icon: Settings, label: "Settings", path: "/admin/settings" },
    { icon: CreditCard, label: "Stripe Config", path: "/admin/stripe-config" },
    { icon: CreditCard, label: "Subscriptions", path: "/admin/subscriptions" },
    { icon: UserCog, label: "User Management", path: "/admin/user-management" },
    { icon: Users, label: "Users", path: "/admin/users" },
    { icon: FileText, label: "Valuation Leads", path: "/admin/leads" },
    { icon: AlertTriangle, label: "Webhook Alerts", path: "/admin/alerts" },
    { icon: Webhook, label: "Webhook Events", path: "/admin/webhooks" },
  ];

  const isActive = (path: string) => location.pathname === path;

  const NavItem = ({ item }: { item: typeof navItems[0] }) => {
    const button = (
      <Button
        variant="ghost"
        className={cn(
          "w-full",
          isCollapsed ? "justify-center px-2" : "justify-start",
          isActive(item.path) && "bg-godyspo-gold/10 text-godyspo-gold hover:bg-godyspo-gold/20"
        )}
      >
        <item.icon className={cn("h-4 w-4", !isCollapsed && "mr-2")} />
        {!isCollapsed && item.label}
      </Button>
    );

    if (isCollapsed) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <Link to={item.path}>{button}</Link>
          </TooltipTrigger>
          <TooltipContent side="right">
            <p>{item.label}</p>
          </TooltipContent>
        </Tooltip>
      );
    }

    return <Link to={item.path}>{button}</Link>;
  };

  return (
    <TooltipProvider delayDuration={0}>
      <div className="min-h-screen flex flex-col md:flex-row bg-godyspo-background">
        {/* Mobile Navigation */}
        {isMobile && <AdminMobileNav />}

        {/* Sidebar - Desktop only */}
        {!isMobile && (
          <aside className={cn(
            "border-r border-border bg-card transition-all duration-300 h-screen flex flex-col",
            isCollapsed ? "w-16" : "w-64"
          )}>
            {/* Fixed Header */}
            <div className={cn("p-6 flex-shrink-0", isCollapsed && "p-3")}>
              <div className={cn(
                "flex items-center",
                isCollapsed ? "flex-col gap-2" : "justify-between"
              )}>
                <div className={cn(
                  "flex items-center",
                  isCollapsed ? "justify-center" : "gap-2"
                )}>
                  <Shield className="h-6 w-6 text-godyspo-gold" />
                  {!isCollapsed && (
                    <h1 className="text-xl font-bold text-godyspo-gold">Admin Console</h1>
                  )}
                </div>
                
                <div className={cn(
                  "flex items-center",
                  isCollapsed ? "flex-col gap-2" : "gap-2"
                )}>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={toggleCollapse}
                    className="h-8 w-8"
                  >
                    {isCollapsed ? (
                      <ChevronRight className="h-4 w-4" />
                    ) : (
                      <ChevronLeft className="h-4 w-4" />
                    )}
                  </Button>
                  {isCollapsed ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div><AdminNotificationBell /></div>
                      </TooltipTrigger>
                      <TooltipContent side="right">
                        <p>Notifications</p>
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    <AdminNotificationBell />
                  )}
                </div>
              </div>
            </div>

            {/* Scrollable Navigation */}
            <nav className={cn(
              "flex-1 overflow-y-auto space-y-2 scrollbar-dark",
              isCollapsed ? "px-3 pb-3" : "px-6 pb-6"
            )}>
              {navItems.map((item) => (
                <NavItem key={item.path} item={item} />
              ))}
              
              <div className="my-4 border-t border-border"></div>
              
              {isCollapsed ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Link to="/dashboard">
                      <Button
                        variant="ghost"
                        className="w-full justify-center px-2 text-muted-foreground hover:text-foreground"
                      >
                        <ArrowLeft className="h-4 w-4" />
                      </Button>
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent side="right">
                    <p>Seller Dashboard</p>
                  </TooltipContent>
                </Tooltip>
              ) : (
                <Link to="/dashboard">
                  <Button
                    variant="ghost"
                    className="w-full justify-start text-muted-foreground hover:text-foreground"
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Seller Dashboard
                  </Button>
                </Link>
              )}
            </nav>
          </aside>
        )}

        {/* Main Content */}
        <main className="flex-1 overflow-auto pb-[env(safe-area-inset-bottom)]">
          <div className="container mx-auto p-4 md:p-6">
            <Outlet />
          </div>
        </main>
      </div>
    </TooltipProvider>
  );
};

export default AdminLayout;
